import type { ProspectLoan } from '../types';

interface LoanContextRowProps {
  loan: ProspectLoan;
}

function trimOrNull(s: string | undefined | null): string | null {
  if (!s) {
    return null;
  }
  const t = String(s).trim();
  return t.length > 0 ? t : null;
}

/**
 * 3-column strip `.loan-ctx` with Investor, Product, Loan Officer.
 * All data comes from typed `ProspectLoan` fields — no external service calls.
 */
export function LoanContextRow({ loan }: LoanContextRowProps) {
  // Investor: investor_name (+ channel suffix as .ctx-dim).
  const investorName = trimOrNull(loan.investor_name ?? null);
  const channel = trimOrNull(loan.channel ?? null);

  // Product: rate_lock_loan_program + cx_doc_type || rate_lock_documentation_type.
  const productParts: string[] = [];
  const program = trimOrNull(loan.rate_lock_loan_program);
  if (program) {
    productParts.push(program);
  }
  const docType = trimOrNull(loan.cx_doc_type) ?? trimOrNull(loan.rate_lock_documentation_type);
  if (docType) {
    productParts.push(docType);
  }
  const product = productParts.length > 0 ? productParts.join(' · ') : '—';

  // Loan Officer: first+last → loan_originator_name → —.
  const composed = [trimOrNull(loan.loan_officer_first_name), trimOrNull(loan.loan_officer_last_name)]
    .filter(Boolean)
    .join(' ')
    .trim();
  const loName = composed.length > 0 ? composed : trimOrNull(loan.loan_originator_name) ?? '—';

  return (
    <section className="loan-ctx" aria-label="Loan context">
      <div className="ctx-col">
        <div className="ctx-k">Investor</div>
        <div className="ctx-v">
          {investorName ?? '—'}
          {investorName && channel && <span className="ctx-dim"> · {channel}</span>}
        </div>
      </div>
      <div className="ctx-col">
        <div className="ctx-k">Product</div>
        <div className="ctx-v">{product}</div>
      </div>
      <div className="ctx-col">
        <div className="ctx-k">Loan Officer</div>
        <div className="ctx-v">{loName}</div>
      </div>
    </section>
  );
}
